<template>
  <div class="fin-table-wrap" :class="{ 'is-reloading': isReloading }">
    <div class="fin-table-toolbar">
      <div class="fin-zoom-group" title="Zmadho ose zvogëlo shkrimin e tabelës">
        <button type="button" class="fin-page-btn" :disabled="zoom <= zoomMin" title="Zvogëlo (Ctrl + rrota e miut)" @click="zoomOut">
          <i class="fa fa-minus"></i>
        </button>
        <button type="button" class="fin-zoom-label" title="Kthe në 100%" @click="zoomReset">{{ zoomLabel }}</button>
        <button type="button" class="fin-page-btn" :disabled="zoom >= zoomMax" title="Zmadho (Ctrl + rrota e miut)" @click="zoomIn">
          <i class="fa fa-plus"></i>
        </button>
      </div>

      <div v-if="hasOverflow" class="fin-hscroll-group">
        <button type="button" class="fin-page-btn" :disabled="!canScrollLeft" title="Kolonat majtas" @click="scrollByDir(-1)">
          <i class="fa fa-chevron-left"></i>
        </button>
        <input class="fin-hscroll-range" type="range" min="0" :max="scrollMax" :value="scrollLeft" :aria-label="'Pozicioni i kolonave'" @input="onRangeInput($event.target.value)" />
        <button type="button" class="fin-page-btn" :disabled="!canScrollRight" title="Kolonat djathtas" @click="scrollByDir(1)">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>

      <span v-if="overflowHint" class="fin-table-toolbar-hint">{{ overflowHint }}</span>
      <span v-else class="fin-table-toolbar-hint is-muted">Ctrl + rrota e miut zmadhon tabelën</span>
    </div>

    <div class="fin-table-scroll-shell" :class="{ 'has-left': canScrollLeft, 'has-right': canScrollRight }" :style="{ '--fin-zoom': String(zoom) }">
      <button v-if="canScrollLeft" type="button" class="fin-hscroll-fab is-left" title="Shiko kolonat majtas" @click="scrollByDir(-1)">
        <i class="fa fa-chevron-left"></i>
      </button>
      <button v-if="canScrollRight" type="button" class="fin-hscroll-fab is-right" title="Shiko kolonat djathtas" @click="scrollByDir(1)">
        <i class="fa fa-chevron-right"></i>
      </button>
      <div ref="scrollEl" class="fin-table-scroll" tabindex="0" @scroll="onScroll" @keydown="onKeydown">
        <slot />
      </div>
    </div>

    <slot name="footer" />
  </div>
</template>

<script>
const ZOOM_KEY = "gobe.financa.tableZoom";
const ZOOM_MIN = 0.7;
const ZOOM_MAX = 1.4;
const ZOOM_STEP = 0.1;

export default {
  name: "FinancaTableViewport",
  props: {
    isReloading: { type: Boolean, default: false },
  },
  data() {
    return {
      zoomMin: ZOOM_MIN,
      zoomMax: ZOOM_MAX,
      zoom: 1,
      scrollLeft: 0,
      scrollMax: 0,
      canScrollLeft: false,
      canScrollRight: false,
    };
  },
  computed: {
    zoomLabel() {
      return Math.round(this.zoom * 100) + "%";
    },
    hasOverflow() {
      return this.scrollMax > 4;
    },
    overflowHint() {
      if (!this.hasOverflow) return "";
      if (this.canScrollLeft && this.canScrollRight) {
        return "Ka më shumë kolona majtas dhe djathtas. Shigjetat, rrëshqitësi, ose Shift + rrota e miut.";
      }
      if (this.canScrollRight) {
        return "Ka më shumë kolona djathtas. Përdor → ose Shift + rrotën e miut.";
      }
      return "Ka më shumë kolona majtas. Përdor ← ose Shift + rrotën e miut.";
    },
  },
  mounted() {
    this.zoom = this.readZoom();
    this.boundWheel = this.onWheel.bind(this);
    this.$nextTick(() => {
      const el = this.$refs.scrollEl;
      if (!el) return;
      el.addEventListener("wheel", this.boundWheel, { passive: false });
      this.resizeObserver = new ResizeObserver(() => this.updateOverflow());
      this.resizeObserver.observe(el);
      const table = el.querySelector("table");
      if (table) this.resizeObserver.observe(table);
      this.updateOverflow();
    });
  },
  updated() {
    this.$nextTick(() => {
      const el = this.$refs.scrollEl;
      if (this.resizeObserver && el) {
        const table = el.querySelector("table");
        if (table) this.resizeObserver.observe(table);
      }
      this.updateOverflow();
    });
  },
  beforeUnmount() {
    const el = this.$refs.scrollEl;
    if (el && this.boundWheel) el.removeEventListener("wheel", this.boundWheel);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    readZoom() {
      try {
        const raw = Number(localStorage.getItem(ZOOM_KEY));
        if (!Number.isFinite(raw)) return 1;
        return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(raw * 10) / 10));
      } catch (e) {
        return 1;
      }
    },
    setZoom(next) {
      const rounded = Math.round(next * 10) / 10;
      this.zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, rounded));
      try {
        localStorage.setItem(ZOOM_KEY, String(this.zoom));
      } catch (e) {
        /* ignore quota / private mode */
      }
      this.$nextTick(this.updateOverflow);
    },
    zoomIn() {
      this.setZoom(this.zoom + ZOOM_STEP);
    },
    zoomOut() {
      this.setZoom(this.zoom - ZOOM_STEP);
    },
    zoomReset() {
      this.setZoom(1);
    },
    updateOverflow() {
      const el = this.$refs.scrollEl;
      if (!el) return;
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      this.scrollMax = max;
      this.scrollLeft = el.scrollLeft;
      this.canScrollLeft = el.scrollLeft > 4;
      this.canScrollRight = max - el.scrollLeft > 4;
    },
    onScroll() {
      this.updateOverflow();
    },
    onRangeInput(value) {
      const el = this.$refs.scrollEl;
      if (!el) return;
      el.scrollLeft = Number(value) || 0;
      this.updateOverflow();
    },
    scrollByDir(dir) {
      const el = this.$refs.scrollEl;
      if (!el) return;
      const amount = Math.max(240, Math.round(el.clientWidth * 0.55));
      el.scrollBy({ left: dir * amount, behavior: "smooth" });
    },
    onWheel(event) {
      const el = this.$refs.scrollEl;
      if (!el) return;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const now = Date.now();
        if (now - (this._lastZoomWheel || 0) < 70) return;
        this._lastZoomWheel = now;
        if (event.deltaY < 0) this.zoomIn();
        else if (event.deltaY > 0) this.zoomOut();
        return;
      }

      if (!this.hasOverflow) return;

      if (event.shiftKey) {
        event.preventDefault();
        el.scrollLeft += event.deltaY + event.deltaX;
        this.updateOverflow();
      }
    },
    onKeydown(event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (!this.hasOverflow) return;
      event.preventDefault();
      this.scrollByDir(event.key === "ArrowLeft" ? -1 : 1);
    },
  },
};
</script>
